import { collection, doc, documentId, endBefore, getCountFromServer, getDoc, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../services/firebase/index";

const functions = getFunctions();

/**
 * Um lote da varredura completa de alunos (usada nos cards de estatísticas e no
 * diretório de busca por nome).
 *
 * Ordena por ID do documento, e NÃO por `Email` como o `getUsers` da tabela: no
 * Firestore, `orderBy` exclui do resultado todo documento que não tem o campo —
 * alunos sem e-mail sumiam da contagem, e a soma dos cards não fechava com o
 * total. O ID sempre existe, então a paginação é completa e confiável.
 */
export const getUsersScanBatch = async ({ pageReference, pageSize }) => {
    const constraints = [orderBy(documentId()), limit(pageSize)];
    if (pageReference) constraints.push(startAfter(pageReference));
    return getDocs(query(collection(firestore, "users"), ...constraints));
};

export const getUsers = async (
    {
        pageReference,
        pageSize,
        isNextPage,
        email,
    }
) => {
    const usersCollection = collection(firestore, 'users');

    const buildQueries = (emailFilter) => {
        const queries = [
            orderBy("Email", "asc")
        ]

        if (!!pageSize) {
            queries.push(limit(pageSize))
        }

        if (!!isNextPage && !!pageReference) {
            queries.push(startAfter(pageReference))
        }

        if (!isNextPage && !!pageReference) {
            queries.push(endBefore(pageReference))
        }

        if (!!emailFilter && emailFilter != "") {
            queries.push(where("Email", "==", emailFilter))
        }

        return queries;
    }

    const term = (email || "").trim();
    let snapshot = await getDocs(query(usersCollection, ...buildQueries(term)));

    // A busca exata é case-sensitive no Firestore. Os fluxos de gravação agora
    // normalizam o e-mail em minúsculas, mas o admin pode digitar com maiúscula
    // (e docs novos são lowercase): antes de devolver vazio — o "aluno sumiu"
    // dos tickets — tenta o termo em minúsculas.
    const lowered = term.toLowerCase();
    if (!!term && snapshot.empty && lowered !== term) {
        snapshot = await getDocs(query(usersCollection, ...buildQueries(lowered)));
    }

    return snapshot;
}


export const getCountUsers = async (
    {
        email,
    }
) => {
    const usersCollection = collection(firestore, 'users');

    // Firestore não permite '==' e '!=' no mesmo campo. Ao filtrar por e-mail exato,
    // usamos apenas o '==' (a contagem é o nº de docs que casam); caso contrário,
    // contamos todos os alunos que possuem e-mail.
    const term = (email || "").trim();
    if (!term) {
        return getCountFromServer(query(usersCollection, where("Email", "!=", null)));
    }

    // Mesmo fallback case-insensitive do getUsers acima.
    let countSnapshot = await getCountFromServer(query(usersCollection, where("Email", "==", term)));
    const lowered = term.toLowerCase();
    if (countSnapshot.data().count === 0 && lowered !== term) {
        countSnapshot = await getCountFromServer(query(usersCollection, where("Email", "==", lowered)));
    }

    return countSnapshot;
}


export const getUserById = (id) => {
    const docRef = doc(firestore, "users", id);
    return getDoc(docRef);
}


export const getUserContent = (userId) => {
    return getDocs(collection(firestore, "users", userId, "contents"));
}

// Datas de criação das contas no Authentication (fonte de verdade da "data de
// cadastro"; o created_at do doc pode ter sido carimbado por um acesso manual).
// Devolve { uid: epochMs }. Falha silenciosa: sem a função publicada, o painel
// segue exibindo o created_at do doc.
export const getAuthCreationDates = async (uids = []) => {
    const unique = [...new Set((uids || []).filter(Boolean))];
    if (!unique.length) return {};

    const callable = httpsCallable(functions, "getAuthCreationDates");
    const creationTimes = {};

    // A função aceita até 1000 uids; 300 por chamada mantém o payload pequeno.
    for (let i = 0; i < unique.length; i += 300) {
        try {
            const response = await callable({ uids: unique.slice(i, i + 300) });
            Object.assign(creationTimes, response?.data?.creationTimes || {});
        } catch (error) {
            console.warn("Não foi possível ler as datas de cadastro do Authentication:", error?.message || error);
            break;
        }
    }

    return creationTimes;
}

export const getHotmartDelayedSubscriptions = (params = {}) => {
    const hotmartGetDelayedSubscriptions = httpsCallable(functions, "hotmartGetDelayedSubscriptions");
    return hotmartGetDelayedSubscriptions(params);
}

export const cancelHotmartSubscriptions = (params = {}) => {
    const hotmartCancelSubscriptions = httpsCallable(functions, "hotmartCancelSubscriptions");
    return hotmartCancelSubscriptions(params);
}